import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, MapPin, Compass, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Redirect to home page with search intent
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const popularPages = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'For Vendors', path: '/vendors', icon: MapPin },
    { name: 'About Us', path: '/about', icon: Compass },
    { name: 'Blog', path: '/blog', icon: Search },
    { name: 'Contact', path: '/contact', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 flex items-center justify-center px-4">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Animated 404 Illustration */}
          <motion.div 
            className="mb-8"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative inline-block">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="text-8xl lg:text-9xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent"
              >
                404
              </motion.div>
              
              {/* Floating delivery icons around 404 */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center"
              >
                🚚
              </motion.div>
              
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                className="absolute -bottom-4 -left-4 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"
              >
                📦
              </motion.div>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Oops! You seem a bit lost 🗺️
            </h1>
            <p className="text-lg text-gray-600 mb-2 max-w-2xl mx-auto">
              Don't worry, even our fastest delivery drivers sometimes take a wrong turn! 
            </p>
            <p className="text-gray-500 mb-8">
              The page <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono text-sm">{location.pathname}</code> doesn't exist.
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-12"
          >
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="What were you looking for?"
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                >
                  Search
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Navigation Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mb-12"
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-6">
              Here are some popular destinations:
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {popularPages.map((page, index) => {
                const IconComponent = page.icon;
                return (
                  <motion.div
                    key={page.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <Link
                      to={page.path}
                      className="group block p-4 bg-white rounded-xl border border-gray-100 hover:border-red-200 hover:shadow-lg transition-all duration-300"
                    >
                      <IconComponent className="w-6 h-6 text-red-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-medium text-gray-700 group-hover:text-red-600 transition-colors">
                        {page.name}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
          >
            <Link to="/">
              <Button className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center">
                <Home className="w-5 h-5 mr-2" />
                Take Me Home
              </Button>
            </Link>
            
            <Button 
              onClick={() => window.history.back()}
              variant="outline"
              className="border-2 border-red-500 text-red-500 px-8 py-3 rounded-xl font-semibold hover:bg-red-500 hover:text-white transition-all duration-300 flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </Button>
          </motion.div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="text-center"
          >
            <p className="text-gray-500 text-sm mb-4">
              Still can't find what you're looking for?
            </p>
            <div className="flex items-center justify-center gap-6">
              <Link 
                to="/contact" 
                className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors text-sm font-medium"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email Support
              </Link>
              <a 
                href="tel:+91-9876543210" 
                className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors text-sm font-medium"
              >
                <Phone className="w-4 h-4 mr-1" />
                Call Us
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
